<template>
  <div class="bg-white p-6 rounded-lg shadow-lg">
    <div class="grid grid-cols-12 gap-4">
      <div :class="$slots.sidebar ? 'col-span-12 lg:col-span-8' : 'col-span-12'">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div class="flex items-center gap-x-2">
              <p class="truncate text-sm font-medium text-gray-500">
                {{ t(`period_count_label${dataSourceSuffix}`) }}
              </p>
              <VTooltip theme="info-tooltip">
                <InformationCircleIcon class="cursor-pointer h-5 w-4 text-gray-400" aria-hidden="true" />
                <template #popper>
                  <span>{{ t(`period_count_tooltip${dataSourceSuffix}`) }}</span>
                </template>
              </VTooltip>
            </div>
            <p class="text-lg pl-0.5 mt-1 tracking-tight text-gray-700">
              {{ analytics?.general?.periodContracts?.current }}
              <span v-if="analytics?.general?.periodContracts?.previous != null" class="text-sm ml-4 text-gray-500">
                {{ analytics.general.periodContracts.previous }}
                {{ comparisonType ? $t(comparisonType) : "" }}
              </span>
              <Variation class="ml-2" :variation="analytics?.general?.periodContracts?.variation" />
            </p>
          </div>
          <slot name="header-right" />
        </div>
        <LineChart v-if="isDailyView" :data="dailyChartData" :options="chartOptions" :chart-height="170" />
        <LineChart v-else-if="isMonthlyView" :data="monthlyChartData" :options="chartOptions" :chart-height="170" />
      </div>
      <div v-if="$slots.sidebar" class="col-span-12 lg:col-span-4 flex flex-col divide-y divide-gray-100">
        <slot name="sidebar" />
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { LineChart, Variation } from "@estaly/ui";
import { InformationCircleIcon } from "@heroicons/vue/outline";

export default {
  components: { LineChart, Variation, InformationCircleIcon },

  props: {
    analytics: { type: Object },
    chartView: { type: String },
    comparisonType: { type: String, default: "previousMonth" },
    dataSource: { type: String, default: "contracts" },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    isContractsView() {
      return this.dataSource === "contracts";
    },

    isLeadsView() {
      return this.dataSource === "leads";
    },

    isGrossSalesView() {
      return this.dataSource === "gross_sales";
    },

    dataSourceSuffix() {
      if (this.isContractsView) return "";
      if (this.isLeadsView) return "_leads";
      if (this.isGrossSalesView) return "_gross_sales";
      return "";
    },

    isDailyView() {
      return this.chartView === "daily";
    },

    isMonthlyView() {
      return this.chartView === "monthly";
    },

    dailyChartData() {
      const daily = this.analytics?.sales?.dailyPlanSales;
      if (!daily?.current) return {};

      const currentData = daily.current.map((entry) => ({ date: entry.date, value: entry.count }));
      let previousData = [];

      if (daily.previous) {
        previousData = currentData.map((entry, index) => ({
          date: entry.date,
          value: daily.previous[index]?.count || 0,
        }));
      }

      return {
        sold: { color: "#8C1EFC", value: currentData },
        "previous contracts sold": { color: "#D1D5DB", value: previousData },
      };
    },

    monthlyChartData() {
      const monthly = this.analytics?.sales?.monthlyPlanSales;
      const currentData = monthly?.current?.map((entry) => ({ date: entry.date, value: entry.count })) || [];

      return {
        sold: { color: "#8C1EFC", value: currentData },
      };
    },

    activeChartData() {
      return this.isDailyView ? this.dailyChartData : this.monthlyChartData;
    },

    maxValue() {
      const datasets = Object.values(this.activeChartData);
      const allValues = datasets.flatMap((d) => (d.value || []).map((p) => p.value));
      return Math.max(1, ...allValues);
    },

    chartOptions() {
      const isDaily = this.isDailyView;
      const data = this.activeChartData;
      const maxVal = isDaily ? this.maxValue : this.maxValue * 1.15;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            title: { display: true, text: isDaily ? "Date" : "Mois" },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              align: "center",
              textAlign: "center",
              callback: (value, index, values) => {
                const step = isDaily ? 4 : 2;
                if (index === 0 || index === values.length - 1 || index % step === 0) {
                  return data["sold"]?.value[index]?.date || value;
                }
                return "";
              },
            },
          },
          y: {
            grid: { display: false, drawBorder: false },
            beginAtZero: true,
            title: { display: true, text: "Ventes" },
            ticks: {
              maxTicksLimit: 2,
              callback: (value) => {
                if (value === 0) return value;
                if (isDaily && value === this.maxValue) return value;
                if (!isDaily && Math.abs(value - this.maxValue * 1.15) < 1) return this.maxValue;
                return "";
              },
            },
            suggestedMax: maxVal,
            max: maxVal,
          },
        },
        plugins: {
          annotation: false,
          datalabels: false,
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => ` ${tooltipItem.dataset.label} : ${tooltipItem.raw.y.toLocaleString()}`,
            },
          },
        },
      };
    },
  },
};
</script>

<i18n src="./SalesChart.i18n.json"></i18n>
